import { Clone } from "@react-three/drei";
import { useImperativeHandle, forwardRef, useRef } from "react";
import { useMemo } from "react";
import {
  BufferGeometry,
  CatmullRomCurve3,
  MeshBasicMaterial,
  Vector3,
  Line,
  LineDashedMaterial,
  AdditiveBlending,
  Color,
  MathUtils,
  LineBasicMaterial,
  Float32BufferAttribute,
  LatheGeometry,
  Vector2,
  BackSide,
  FrontSide,
} from "three";
import { smoothstep } from "three/src/math/MathUtils";
import { findAllObjectsStartsWithKey } from "../../../../utils/FindObjects";
import DynamicTube from "../../DynamicTube";
import FresnelMaterial from "../../materials/FresnelMaterial";

const MagneticField = forwardRef(({ scene, subdivisions = 32, segments = 32, brightness = 3, ...props }, ref) => {
  const groupRef = useRef();
  const latheRef = useRef();
  const latheRef2 = useRef();
  const latheRef3 = useRef();

  const [line, mat, lineParentPosition, latheGeometry] = useMemo(() => {
    var points = findAllObjectsStartsWithKey("MFLine_0_Point", scene);
    var lineParent = points[0].parent;
    var lineParentPosition = new Vector3();
    lineParent.getWorldPosition(lineParentPosition);

    // points.sort((a, b) => {
    //   var intA = parseInt(
    //     a.name.replace("MFLine_0_Point_(", "").replace(")", "")
    //   );
    //   var intB = parseInt(
    //     b.name.replace("MFLine_0_Point_(", "").replace(")", "")
    //   );

    //   if (intA < intB) {
    //     return -1;
    //   }
    //   if (intA > intB) {
    //     return 1;
    //   }
    //   return 0;
    // });

    if (!lineParent.userData.mirrored) {
      var nIdx = points.length;

      for (let index = points.length - 1; index >= 0; index--) {
        const element = points[index];
        var newPoint = element.clone();
        lineParent.add(newPoint);
        newPoint.position.y = points[0].position.y - (newPoint.position.y - points[0].position.y);
        points[nIdx] = newPoint;
        element.name = `MFLine_0_Point_(${nIdx})`;
        nIdx++;
      }

      // points = findAllObjectsStartsWithKey("MFLine_0_Point", scene);

      lineParent.userData.mirrored = true;
    }

    const vectors = [];
    points.forEach(() => vectors.push(new Vector3()));
    points.forEach((p, i) => {
      vectors[i] = p.position.clone();
      // p.getWorldPosition(vectors[i]);
    });

    const spline = new CatmullRomCurve3(vectors);

    const samples = spline.getPoints(points.length * subdivisions);
    const geometrySpline = new BufferGeometry().setFromPoints(samples);

    var lathePoints = [];
    samples.forEach((s, i) => (lathePoints[i] = new Vector2(s.z, s.y)));

    const latheGeometry = new LatheGeometry(lathePoints, subdivisions);

    const color = new Color();

    let colors1 = [];

    for (let i = 0; i < points.length * subdivisions; i++) {
      let t = i / (points.length * subdivisions);
      t = smoothstep(t, 0.4, 0.6);

      // color.setHSL(i / points.length * subdivisions, 1.0, 0.5);
      color.lerpColors(new Color("#ffc400").multiplyScalar(brightness), new Color("#2e4661").multiplyScalar(brightness * 1.5), t);
      colors1.push(color.r, color.g, color.b);
    }

    geometrySpline.setAttribute("color", new Float32BufferAttribute(colors1, 3));

    var mat = new LineBasicMaterial({
      color: "#ffffff",
      // dashSize: 0.08,
      // gapSize: 0.09,
      depthTest: false,
      depthWrite: false,
      transparent: true,
      opacity: 0.5,
      // blending: AdditiveBlending,
      vertexColors: true,
    });

    const line = new Line(geometrySpline, mat);
    line.computeLineDistances();

    return [line, mat, lineParentPosition, latheGeometry];
  }, []);

  useImperativeHandle(ref, () => ({
    setOpacity: (v) => {
      let s = smoothstep(v * 0.5 + 0.1, 0.1, 1);
      if (mat) {
        // console.log(v);
        mat.opacity = s * 0.7;
        // mat.color = new Color(s * 5, s * 5, s * 5);
        // mat.color = new Color(v, v, v);
      }
      if (latheRef.current) {
        latheRef.current.material.opacity = s * 0.2;
      }
    },
    setFlipped: (flipped) => {
      if (groupRef.current) {
        if (flipped) {
          groupRef.current.scale.y = -1;
        } else {
          groupRef.current.scale.y = 1;
        }
      }
    },
  }));

  const cloneAngles = useMemo(() => {
    var angles = [];
    for (let index = 0; index < segments; index++) {
      angles.push((index / segments) * 360);
    }
    return angles;
  }, []);

  return (
    <group position={lineParentPosition} ref={groupRef} {...props}>
      <group>
        {/* <mesh>
          <sphereGeometry></sphereGeometry>
        </mesh> */}
        {cloneAngles.map((a, idx) => (
          <Clone object={line} rotation-y={MathUtils.degToRad(a)} key={`Magneticfieldline_${idx}`} />
        ))}
        {/* <primitive object={line} />
        <Clone object={line} rotation-y={MathUtils.degToRad(60)} />
        <Clone object={line} rotation-y={MathUtils.degToRad(90)} />
        <Clone object={line} rotation-y={MathUtils.degToRad(180)} />
        <Clone object={line} rotation-y={MathUtils.degToRad(270)} /> */}
        <mesh ref={latheRef}>
          <primitive object={latheGeometry} attach="geometry" />
          <FresnelMaterial
            depthTest={false}
            depthWrite={false}
            opacity={1}
            transparent
            uBaseColor="orange"
            // uFresnelColor="#013c80"
            uFresnelColor="#013c80"
            // uBaseColor="orange"
            // uFresnelColor="#ffe9a2"
            uFresnelPower={0.6}
            blending={AdditiveBlending}
            side={FrontSide}
          />
        </mesh>
      </group>
    </group>
  );
});

export default MagneticField;
